<template>
  <v-container fluid>
    <v-overlay :value="loading" absolute>
          <v-progress-circular
            class="my-4"
            :size="60"
            width="5"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </v-overlay>
    <v-row align="center" justify="center" class="mt-4">
      <v-card>
        <v-card-text>
          <div class="layout ma-0 align-center column">
            <v-row align="end">
              <v-spacer />
              <img
                v-bind:src="orgLogo"
                v-bind:alt="organization.name"
                style="max-width: 160px; max-height: 60px"
                class="mr-2"
              />
            </v-row>
            <v-row align="center">
              <v-spacer />
              <span>{{ organization.name }}</span>
            </v-row>
          </div>

          <v-data-table
            :hide-default-header="true"
            :hide-default-footer="true"
            :items="dashboardItems"
            class="elevation-0 table-striped"
            :items-per-page="20"
          >
            <template v-slot:item="props">
              <tr>
                <td class="text-xs-left">
                  <v-icon>{{ props.item.icon }}</v-icon>
                  <span v-t="props.item.text"></span>
                </td>
                <td class="text-xs-right">
                  <v-progress-linear
                    v-if="props.item.progress"
                    :value="props.item.pvalue"
                    :height="6"
                    :color="props.item.color"
                  ></v-progress-linear>
                  <span v-if="props.item.progress" class="caption">{{
                    props.item.value
                  }}</span>
                  <v-chip
                    v-if="!props.item.progress && !isNaN(props.item.value)"
                    :color="props.item.color"
                    text-color="white"
                    >{{ props.item.value }}</v-chip
                  >
                  <v-avatar
                    size="30"
                    :color="props.item.color"
                    v-if="isNaN(props.item.value) && !props.item.progress"
                  >
                    <v-icon :color="'white'">
                      {{ props.item.value }}
                    </v-icon>
                  </v-avatar>
                </td>
              </tr>
            </template>
            <template v-slot:no-data>No data</template>
          </v-data-table>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                absolute
                dark
                fab
                top
                right
                color="blue-grey"
                @click="onSettingsEdit"
              >
                <v-icon>mdi-cog-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Settings") }}</span>
          </v-tooltip>
        </v-card-text>
        <settings-modal :ref="modalKey" />
      </v-card>
    </v-row>
  </v-container>
</template>
<script>
import SettingsModal from '@/components/administration/organization/SettingsModal.vue';
import OrganizationService from '@/services/OrganizationService';
import CurrentOrganizationStatistics from '@/ecm/models/CurrentOrganizationStatistics';
export default {
    components: {
        SettingsModal,
    },
    async created() {
        this.loading = true;
        const currentOrganization = await OrganizationService.getCurrent();
        this.organization = new CurrentOrganizationStatistics(currentOrganization);
        this.orgLogo = await OrganizationService.getLogo(this.organization.id);
        this.loading = false;
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modalKey];
        },
        dashboardItems() {
            return [
                {
                    icon: 'mdi-file-document',
                    text: 'Documents',
                    value: this.organization.totalDocuments,
                    color: 'primary',
                    progress: false,
                },
                {
                    icon: 'mdi-clipboard-check',
                    text: 'SignedDocuments',
                    value: this.organization.totalSignedDocuments,
                    color: 'primary',
                    progress: false,
                },
                {
                    icon: 'mdi-ballot',
                    text: 'ContentTypes',
                    value: this.organization.totalContentTypes,
                    color: 'primary',
                    progress: false,
                },
                {
                    icon: 'mdi-account-multiple',
                    text: 'Users',
                    value: this.organization.totalUsers,
                    color: 'primary',
                    progress: false,
                },
                {
                    icon: 'mdi-account-star',
                    text: 'AutorecognitionLicense',
                    value: this.organization.autorecognitionEnabled
                        ? 'mdi-check-circle'
                        : 'mdi-close-circle',
                    color: 'primary',
                    progress: false,
                },
                {
                    icon: 'mdi-account-multiple',
                    text: 'FullAccessUsers',
                    value:
                        this.organization.totalFullUsers +
                        '/' +
                        this.organization.maxFullUsers,
                    pvalue:
                        (this.organization.totalFullUsers * 100) /
                        this.organization.maxFullUsers,
                    color:
                        this.organization.maxFullUsers == 0
                            ? 'grey lighten-1'
                            : (this.organization.totalFullUsers * 100) /
                                this.organization.maxFullUsers ==
                                100
                                ? 'red'
                                : (this.organization.totalFullUsers * 100) /
                                    this.organization.maxFullUsers >=
                                    50
                                    ? 'orange'
                                    : 'green',
                    progress: true,
                },
                {
                    icon: 'mdi-account-multiple',
                    text: 'ReadonlyUsers',
                    value:
                        this.organization.totalReadonlyUsers +
                        '/' +
                        this.organization.maxReadonlyUsers,
                    pvalue:
                        (this.organization.totalReadonlyUsers * 100) /
                        this.organization.maxReadonlyUsers,
                    color:
                        this.organization.maxReadonlyUsers == 0
                            ? 'grey lighten-1'
                            : (this.organization.totalReadonlyUsers * 100) /
                                this.organization.maxReadonlyUsers ==
                                100
                                ? 'red'
                                : (this.organization.totalReadonlyUsers * 100) /
                                    this.organization.maxReadonlyUsers >=
                                    50
                                    ? 'orange'
                                    : 'green',
                    progress: true,
                },
                {
                    icon: 'mdi-account-multiple',
                    text: 'ConcurrentUsers',
                    value:
                        this.organization.totalConcurrentUsers +
                        '/' +
                        this.organization.maxConcurrentUsers,
                    pvalue:
                        (this.organization.totalConcurrentUsers * 100) /
                        this.organization.maxConcurrentUsers,
                    color:
                        this.organization.maxConcurrentUsers == 0
                            ? 'grey lighten-1'
                            : (this.organization.totalConcurrentUsers * 100) /
                                this.organization.maxConcurrentUsers ==
                                100
                                ? 'red'
                                : (this.organization.totalConcurrentUsers * 100) /
                                    this.organization.maxConcurrentUsers >=
                                    50
                                    ? 'orange'
                                    : 'green',
                    progress: true,
                },
                {
                    icon: 'mdi-cellphone-lock',
                    text: 'TwoFactorAuthenticationUsers',
                    value:
                        this.organization.total2FAUsers +
                        '/' +
                        this.organization.max2FAUsers,
                    pvalue:
                        (this.organization.total2FAUsers * 100) /
                        this.organization.max2FAUsers,
                    color:
                        this.organization.max2FAUsers == 0
                            ? 'grey lighten-1'
                            : (this.organization.total2FAUsers * 100) /
                                this.organization.max2FAUsers ==
                                100
                                ? 'red'
                                : (this.organization.total2FAUsers * 100) /
                                    this.organization.max2FAUsers >=
                                    50
                                    ? 'orange'
                                    : 'green',
                    progress: true,
                },
                {
                    icon: 'mdi-cellphone-android',
                    text: 'RegisteredDevices',
                    value:
                        this.organization.totalDevices + '/' + this.organization.maxDevices,
                    pvalue:
                        (this.organization.totalDevices * 100) /
                        this.organization.maxDevices,
                    color:
                        this.organization.maxDevices == 0
                            ? 'grey lighten-1'
                            : (this.organization.totalDevices * 100) /
                                this.organization.maxDevices ==
                                100
                                ? 'red'
                                : (this.organization.totalDevices * 100) /
                                    this.organization.maxDevices >=
                                    50
                                    ? 'orange'
                                    : 'green',
                    progress: true,
                },
                {
                    icon: 'mdi-nas',
                    text: 'StorageUse',
                    value:
                        this.organization.totalUsedSpaceDisplay +
                        '/' +
                        this.organization.maxUsedSpaceDisplay,
                    pvalue:
                        (this.organization.totalUsedSpace * 100) /
                        this.organization.maxUsedSpace,
                    color:
                        this.organization.maxUsedSpace == 0
                            ? 'grey lighten-1'
                            : (this.organization.totalUsedSpace * 100) /
                                this.organization.maxUsedSpace ==
                                100
                                ? 'red'
                                : (this.organization.totalUsedSpace * 100) /
                                    this.organization.maxUsedSpace >=
                                    50
                                    ? 'orange'
                                    : 'green',
                    progress: true,
                },
            ];
        },
    },
    data() {
        return {
            modalKey: 'modalSettings',
            organization: new CurrentOrganizationStatistics(),
            loading: false,
            orgLogo: null,
        };
    },
    methods: {
        onSettingsEdit() {
            this.modalInstance.new();
        },
    },
};
</script>
